import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../../context/LanguageContext';

// Strip old domain, ensure leading slash for relative paths
const normalizeImgUrl = (raw) => {
    if (!raw) return null;
    const m = raw.match(/^https?:\/\/(?:www\.)?(?:staging\.)?nizus\.com(\/.*)/i);
    if (m) return m[1];
    if (!raw.startsWith('http') && !raw.startsWith('/')) return '/' + raw;
    return raw;
};

// Strip language prefix (fr/, en/), .html/.htm extension, take last path segment
const cleanSlug = (raw) => {
    if (!raw) return '';
    let s = raw.trim().replace(/\.html?$/i, '');
    s = s.replace(/^(fr|en)\//i, '');
    return s.split('/').filter(Boolean).pop() || s;
};

const BlogCard = ({ post }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { language } = useLanguage();
    // Public : préfixe de langue OBLIGATOIRE (route = /:lang/blog/:slug) sinon page blanche.
    const blogBase = location.pathname.startsWith('/dashboard') ? '/dashboard/account/blog' : `/${language}/blog`;

    const title      = language === 'en' ? post.title_en      : post.title_fr;
    const preview    = language === 'en' ? post.previewText_en : post.previewText_fr;
    const category   = language === 'en' ? post.category_en   : post.category_fr;
    const previewImg = post.computed_preview_img
        || normalizeImgUrl(language === 'en' ? post.previewimg_en : post.previewimg_fr);
    const rawSlug    = language === 'en' ? (post.url_en || post.url_fr) : (post.url_fr || post.url_en);
    const slug       = cleanSlug(rawSlug);

    const rawKeywords = language === 'en' ? (post.keywords_en || post.keywords_fr) : (post.keywords_fr || post.keywords_en);
    const keywords    = rawKeywords ? rawKeywords.split(',').map(k => k.trim()).filter(Boolean) : [];

    const handleClick = () => navigate(`${blogBase}/${slug}`);

    return (
        <article
            onClick={handleClick}
            className="group bg-white dark:bg-gray-900 rounded-2xl overflow-hidden border border-gray-200 dark:border-white/10 hover:border-blue-300 dark:hover:border-blue-500/50 transition-all duration-200 cursor-pointer hover:shadow-lg hover:shadow-blue-500/10"
        >
            {previewImg && (
                <div className="h-48 overflow-hidden bg-gray-100 dark:bg-gray-800">
                    <img
                        src={previewImg}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                </div>
            )}
            {!previewImg && (
                <div className="h-48 bg-gradient-to-br from-blue-500/10 to-blue-600/20 dark:from-blue-900/30 dark:to-blue-800/20 flex items-center justify-center">
                    <svg className="w-12 h-12 text-blue-400/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h6m-6-4h4" />
                    </svg>
                </div>
            )}
            <div className="p-5">
                {category && (
                    <span className="inline-block text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide mb-2">
                        {category}
                    </span>
                )}
                <h2 className="font-bold text-gray-900 dark:text-white text-base leading-snug mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                    {title || '—'}
                </h2>
                {keywords.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {keywords.slice(0, 5).map(kw => (
                            <span key={kw} className="inline-block text-xs border border-blue-400 dark:border-blue-500 text-blue-600 dark:text-blue-400 rounded-full px-2 py-0.5">
                                {kw}
                            </span>
                        ))}
                    </div>
                )}
                {preview && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-3 leading-relaxed">
                        {preview}
                    </p>
                )}
                <div className="mt-4 flex items-center gap-1 text-sm font-semibold text-blue-600 dark:text-blue-400">
                    <span>{language === 'en' ? 'Read' : 'Lire'}</span>
                    <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                </div>
            </div>
        </article>
    );
};

export default BlogCard;
